import React from "react";
import { useSearchParams } from "react-router-dom";
import { Layers, Target, Sparkles, Users, Image, LayoutList, Send, AtSign, FileText } from "lucide-react";

// Relative imports deliberately, not the "@/" alias: it is a webpack-only
// alias in this repo (craco.config.js's webpack `resolve.alias`) with no
// corresponding Jest moduleNameMapper, so it does not resolve under `yarn
// test` -- confirmed empirically for EMAILS-IMPL-001, still true here.
import { usePageTitle } from "../../contexts/PageTitleContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import ProgramsPanel from "./programs/ProgramsPanel";
import CampaignsPanel from "./campaigns/CampaignsPanel";
import AnglesPanel from "./angles/AnglesPanel";
import AudiencesPanel from "./audiences/AudiencesPanel";
import AssetsShell from "./assets/AssetsShell";
import CreativesPanel from "./creatives/CreativesPanel";
import SenderConfigurationsPanel from "./senders/SenderConfigurationsPanel";
import FooterConfigurationsPanel from "./footers/FooterConfigurationsPanel";
import BroadcastsPanel from "./broadcasts/BroadcastsPanel";

// EMAILS-IMPL-007: turns the EMAILS-IMPL-001 placeholder shell into the
// first operational catalog-authoring surface -- Programs/Campaigns/Angles/
// Audiences. EMAILS-IMPL-009 activates Assets (all 9 governed reusable
// Asset families, sub-navigated inside AssetsShell -- see that file).
// EMAILS-IMPL-011 activates Creatives (governed composition authoring,
// referencing Asset Versions from the 9 families above -- see
// creatives/CreativesPanel.jsx). EMAILS-IMPL-020 activates Broadcasts
// (governed execution authoring + the dedicated GHL Handoff view -- see
// broadcasts/BroadcastsPanel.jsx), completing the Day-1 functional
// workflow. EMAILS-IMPL-021 activates Senders (governed, Environment-
// scoped Sender Configuration authoring -- see
// senders/SenderConfigurationsPanel.jsx; BRD-SPEC-001 §10.2), placed
// immediately before Broadcasts since a Broadcast's own Sender
// Configuration selector depends on this tenant-wide reference catalog
// already existing. Builder is still deliberately NOT listed as a
// top-level tab (see EMAILS-IMPL-001's original reasoning, unchanged):
// it is expected to be reached from Creative authoring, not exist as its
// own governed area.
//
// Navigation pattern: a single flat `Tabs` synced to a `?tab=` query param,
// the same established mechanism AnalyticsWarRoom.jsx (War Room) already
// uses for its own primary sections -- not War Room's own bespoke grouped/
// dropdown nav (WarRoomPrimaryNav.jsx), which solves a different problem
// (12 destinations consolidated into 5 groups) this module's flat tab set
// doesn't have.
const SECTIONS = [
  { value: "programs", label: "Programs", icon: Layers, operational: true },
  { value: "campaigns", label: "Campaigns", icon: Target, operational: true },
  { value: "angles", label: "Angles", icon: Sparkles, operational: true },
  { value: "audiences", label: "Audiences", icon: Users, operational: true },
  { value: "assets", label: "Assets", icon: Image, operational: true },
  { value: "creatives", label: "Creatives", icon: LayoutList, operational: true },
  { value: "senders", label: "Senders", icon: AtSign, operational: true },
  { value: "footers", label: "Footers", icon: FileText, operational: true },
  { value: "broadcasts", label: "Broadcasts", icon: Send, operational: true },
];

const DEFAULT_TAB = "programs";
const OPERATIONAL_VALUES = new Set(SECTIONS.filter((s) => s.operational).map((s) => s.value));

export default function EmailsShell() {
  usePageTitle("Emails");

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = OPERATIONAL_VALUES.has(requestedTab) ? requestedTab : DEFAULT_TAB;

  const handleTabChange = (value) => {
    if (!OPERATIONAL_VALUES.has(value)) return; // disabled triggers are inert regardless
    const next = new URLSearchParams(searchParams);
    if (value === DEFAULT_TAB) next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  return (
    // EMAIL-DASHBOARD-KPI-001: the redundant "Emails" intro Card (envelope
    // icon + "Emails" title + "Build, organize, and govern..." subtitle,
    // data-testid="emails-shell-intro") that used to render here is
    // removed -- it duplicated AppShell.jsx's own primary branded page
    // header (logo + tenant name + usePageTitle("Emails") above), which is
    // untouched. Navigation now sits directly below this wrapper's own
    // existing `space-y-6` spacing, no new layout wrapper needed.
    <div className="space-y-6" data-testid="emails-shell">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-white/5 p-1" data-testid="emails-nav">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <TabsTrigger
                key={section.value}
                value={section.value}
                disabled={!section.operational}
                className="gap-1.5 data-[state=active]:bg-violet-500/20 data-[state=active]:text-violet-200"
                data-testid={`emails-nav-${section.value}`}
                title={section.operational ? undefined : "Coming soon"}
              >
                <Icon className="h-4 w-4" />
                {section.label}
                {!section.operational && (
                  <span className="ml-1 rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-400">
                    Soon
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="programs" data-testid="emails-tab-content-programs">
          <ProgramsPanel />
        </TabsContent>
        <TabsContent value="campaigns" data-testid="emails-tab-content-campaigns">
          <CampaignsPanel />
        </TabsContent>
        <TabsContent value="angles" data-testid="emails-tab-content-angles">
          <AnglesPanel />
        </TabsContent>
        <TabsContent value="audiences" data-testid="emails-tab-content-audiences">
          <AudiencesPanel />
        </TabsContent>
        <TabsContent value="assets" data-testid="emails-tab-content-assets">
          <AssetsShell />
        </TabsContent>
        <TabsContent value="creatives" data-testid="emails-tab-content-creatives">
          <CreativesPanel />
        </TabsContent>
        <TabsContent value="senders" data-testid="emails-tab-content-senders">
          <SenderConfigurationsPanel />
        </TabsContent>
        <TabsContent value="footers" data-testid="emails-tab-content-footers">
          <FooterConfigurationsPanel />
        </TabsContent>
        <TabsContent value="broadcasts" data-testid="emails-tab-content-broadcasts">
          <BroadcastsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
